<template>
  <div class="simple-job-queue" data-testid="simple-job-queue">
    <h2 class="text-h6 mb-3">{{ $t('simple_mode.queue_title') }}</h2>

    <div
      v-if="displayJobs.length === 0"
      class="simple-job-queue__empty text-body-2 text-medium-emphasis"
      data-testid="simple-job-queue-empty"
    >
      {{ $t('simple_mode.queue_empty') }}
    </div>

    <div v-else class="simple-job-queue__panel">
      <div
        v-for="job in displayJobs"
        :key="job.id"
        class="simple-job-queue__row"
        data-testid="simple-job-card"
      >
        <div class="simple-job-queue__row-body">
          <div class="text-body-2 font-weight-medium">{{ titleForJob(job) }}</div>
          <v-progress-linear
            v-if="isRunningJob(job)"
            class="mt-2 mb-1"
            :model-value="job.progress || 0"
            height="6"
            rounded
            color="primary"
          />
          <div class="text-caption text-medium-emphasis">
            {{ statusForJob(job) }}
          </div>
        </div>
        <v-btn
          icon
          size="x-small"
          variant="text"
          class="simple-job-queue__cancel"
          :title="$t('simple_mode.queue_cancel')"
          data-testid="simple-job-cancel"
          @click="cancelJob(job)"
        >
          <v-icon size="small">mdi-close</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import createSocket from '@/utils/socket';
import {
  assetCountForJob,
  isRunningJob,
} from '@/components/myassets/myAssetsJobUtils';
import MyAssetsJobsWebSocketMixin from '@/mixins/MyAssetsJobsWebSocketMixin';

export default {
  name: 'SimpleJobQueueStrip',
  mixins: [MyAssetsJobsWebSocketMixin],
  data() {
    return {
      trainJobs: [],
      trainingsSocket: null,
      trainingsChannel: null,
    };
  },
  computed: {
    analysisJobs() {
      const llm = this.$store.getters['project_live_jobs/llm_jobs'] || [];
      const run = this.$store.getters['project_live_jobs/run_model_jobs'] || [];
      return [...llm, ...run];
    },
    activeTrainJobs() {
      return (this.trainJobs || []).filter((job) => job && job.processed === 0 && !job.deleted_at);
    },
    displayJobs() {
      return [...this.analysisJobs, ...this.activeTrainJobs].slice(0, 12);
    },
  },
  async mounted() {
    await this.ensureProjectLiveJobsSubscription();
    await this.loadTrainJobs();
    this.setupTrainingsSocket();
  },
  beforeUnmount() {
    this.teardownTrainingsSocket();
  },
  methods: {
    isRunningJob,
    titleForJob(job) {
      if (job.task === 'train') {
        return this.$t('simple_mode.queue_training');
      }
      if (job.task === 'run_model') {
        return this.$t('simple_mode.queue_run_model');
      }
      const total = Math.max(1, assetCountForJob(job) || 1);
      const done = Math.min(total, Math.round((total * (job.progress || 0)) / 100));
      return this.$t('simple_mode.queue_analyzing', { done, total });
    },
    statusForJob(job) {
      if (isRunningJob(job)) {
        return this.$t('simple_mode.queue_processing');
      }
      return this.$t('simple_mode.queue_waiting');
    },
    async loadTrainJobs() {
      try {
        this.trainJobs = await this.$api.job.get_active_jobs('train') || [];
      } catch {
        this.trainJobs = [];
      }
    },
    trainingsWebsocketChannel() {
      const projectId = sessionStorage.getItem('project_id');
      if (!projectId || projectId === 'undefined') {
        return null;
      }
      return `${projectId}_trainings`;
    },
    setupTrainingsSocket() {
      const channel = this.trainingsWebsocketChannel();
      if (!channel) {
        return;
      }
      if (this.trainingsChannel === channel && this.trainingsSocket) {
        return;
      }
      this.teardownTrainingsSocket();
      this.trainingsChannel = channel;
      const socket = createSocket();
      this.trainingsSocket = socket;
      socket.on('connect', () => {
        socket.emit('subscribe', channel);
      });
      socket.on('message', (message) => {
        let payload = null;
        try {
          payload = JSON.parse(JSON.parse(message));
        } catch (e) {
          return;
        }
        if (!payload || payload.type !== 'train_job_update' || !payload.job) {
          return;
        }
        this.applyTrainJobUpdate(payload.job);
      });
    },
    teardownTrainingsSocket() {
      if (this.trainingsSocket) {
        this.trainingsSocket.close();
      }
      this.trainingsSocket = null;
      this.trainingsChannel = null;
    },
    applyTrainJobUpdate(job) {
      if (job.deleted_at || job.processed === 1) {
        this.trainJobs = this.trainJobs.filter((j) => j.id !== job.id);
        return;
      }
      const idx = this.trainJobs.findIndex((j) => j.id === job.id);
      if (idx >= 0) {
        const next = this.trainJobs.slice();
        next[idx] = job;
        this.trainJobs = next;
      } else {
        this.trainJobs = [...this.trainJobs, job];
      }
    },
    async cancelJob(job) {
      try {
        await this.$api.job.delete(job);
        if (job.task === 'train') {
          this.trainJobs = this.trainJobs.filter((j) => j.id !== job.id);
        }
      } catch {
        this.$store.dispatch('snackbars/add_failed_snack', this.$t('simple_mode.queue_cancel_failed'));
      }
    },
  },
};
</script>

<style scoped>
.simple-job-queue__panel,
.simple-job-queue__empty {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: #fafafa;
}

.simple-job-queue__empty {
  padding: 16px 12px;
}

.simple-job-queue__panel {
  padding: 8px 0;
}

.simple-job-queue__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
}

.simple-job-queue__row + .simple-job-queue__row {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.simple-job-queue__row-body {
  flex: 1 1 auto;
  min-width: 0;
}

.simple-job-queue__cancel {
  flex-shrink: 0;
  margin-top: 2px;
}
</style>
